import { makeStyles } from '@material-ui/core/styles';

/**
 * Estilos comunes para los módulos de Reportes, B2B y Administración
 * Este archivo centraliza los estilos compartidos entre estos módulos para mantener consistencia
 */
export const commonModuleStyles = makeStyles((theme) => ({
  // ============================================
  // Estilos de Layout y Contenedores
  // ============================================
  cardHeader: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  card: {
    backgroundColor: theme.palette.background?.paper || theme.palette.colors?.white || '#FFFFFF',
    padding: theme.spacing(3),
    outline: 'none',
    borderRadius: theme.shape.borderRadius,
    minWidth: 400,
    maxWidth: 600,
    boxShadow: theme.shadows[5],
  },
  CardTable: {
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
    marginTop: theme.spacing(-0.5),
  },
  section: {
    marginBottom: theme.spacing(3),
  },
  formSection: {
    marginBottom: theme.spacing(3),
  },

  // ============================================
  // Estilos de Títulos y Tipografía
  // ============================================
  sectionTitle: {
    marginBottom: theme.spacing(2),
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  title: {
    fontWeight: 600,
    marginBottom: theme.spacing(2),
  },

  // ============================================
  // Estilos de Búsqueda y Filtros
  // ============================================
  search: {
    padding: theme.spacing(0.5, 1),
    display: 'flex',
    alignItems: 'center',
    backgroundColor: theme.palette.background?.paper || theme.palette.colors?.white || '#FFFFFF',
    borderRadius: theme.shape.borderRadius,
    border: 'none',
    boxShadow: 'none',
    '&:focus-within': {
      border: `1px solid ${theme.palette.colors?.fillStroke?.[200] || '#E0E0E0'}`,
    },
  },
  iconButton: {
    padding: theme.spacing(1),
    color: theme.palette.text.secondary,
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
  textArea: {
    marginLeft: theme.spacing(1),
    flex: 1,
    color: theme.palette.text.primary,
    '& .MuiInputBase-input': {
      padding: theme.spacing(1),
    },
  },
  bioText: {
    color: theme.palette.text?.primary || theme.palette.colors?.darkBlack?.[500] || '#000000',
  },
  buttons: {
    padding: theme.spacing(0.5, 1),
    display: 'flex',
    alignItems: 'center',
    backgroundColor: theme.palette.background?.paper || theme.palette.colors?.white || '#FFFFFF',
    borderRadius: theme.shape.borderRadius,
    border: 'none',
    boxShadow: 'none',
  },
  button: {
    margin: theme.spacing(0.5),
    textTransform: 'none',
  },
  filterButton: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    border: 'none',
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
      border: 'none',
    },
  },
  filterButtonOutlined: {
    border: 'none',
    '&:hover': {
      border: 'none',
    },
  },
  grid: {
    marginTop: theme.spacing(2),
  },
  datePicker: {
    width: '100%',
  },

  // ============================================
  // Estilos de Tablas
  // ============================================
  table: {
    [theme.breakpoints.down('lg')]: {
      maxHeight: '500px !important'
    },
    [theme.breakpoints.up('xl')]: {
      maxHeight: '650px !important'
    }
  },

  // ============================================
  // Estilos de Modales
  // ============================================
  modal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalStudent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  paper: {
    backgroundColor: theme.palette.background?.paper || theme.palette.colors?.white || '#FFFFFF',
    border: `0.5px solid ${theme.palette.colors?.fillStroke?.[200] || '#E0E0E0'}`,
    boxShadow: theme.shadows[5],
    padding: theme.spacing(3),
    borderRadius: theme.shape.borderRadius,
  },
  modalCard: {
    padding: theme.spacing(0),
    width: 500,
    minHeight: 250,
    backgroundColor: theme.palette.background?.paper || theme.palette.colors?.white || '#FFFFFF',
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[5],
  },

  // ============================================
  // Estilos de Botones de Acción
  // ============================================
  actionButtons: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(2),
    marginTop: theme.spacing(3),
    paddingTop: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.colors?.fillStroke?.[200] || theme.palette.colors?.gray?.[300] || '#E0E0E0'}`,
  },
  actionButtonContainer: {
    marginTop: theme.spacing(2),
    paddingTop: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.colors?.fillStroke?.[200] || theme.palette.colors?.gray?.[300] || '#E0E0E0'}`,
  },
  buttonsContainer: {
    maxWidth: "100%",
    marginTop: theme.spacing(2),
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(2),
  },
  dangerButton: {
    backgroundColor: theme.palette.error.main,
    marginRight: theme.spacing(1),
    '&:hover': {
      backgroundColor: theme.palette.error.dark,
    },
  },
  cancelButton: {
    color: theme.palette.text?.primary || theme.palette.colors?.darkBlack?.[500] || '#000000',
    borderColor: theme.palette.colors?.fillStroke?.[200] || '#E0E0E0',
    '&:hover': {
      borderColor: theme.palette.colors?.fillStroke?.[300] || '#C0C0C0',
      backgroundColor: 'transparent',
    },
  },

  // ============================================
  // Estilos específicos de Administración
  // ============================================
  moduleItem: {
    paddingLeft: theme.spacing(2),
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(0.5),
  },
  nestedItem: {
    paddingLeft: theme.spacing(4),
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(0.5),
    position: 'relative',
    '&::before': {
      content: '""',
      position: 'absolute',
      left: theme.spacing(2.5),
      top: 0,
      bottom: 0,
      width: '1px',
      backgroundColor: theme.palette.colors?.gray?.[300] || '#e0e0e0',
    },
  },
  checkboxLabel: {
    marginLeft: theme.spacing(1),
  },
  dataScopeContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
  },
  dataScopeSection: {
    flex: 1,
  },
  expandIcon: {
    fontSize: 16,
    marginRight: theme.spacing(1),
    cursor: 'pointer',
  },

  // ============================================
  // Estilos específicos de Formularios
  // ============================================
  infoRow: {
    marginBottom: theme.spacing(1),
    '& .MuiTypography-body2': {
      color: theme.palette.text.secondary,
    },
  },
  sectionContainer: {
    marginBottom: theme.spacing(2),
  },
  formRow: {
    marginBottom: theme.spacing(2),
  },
  sectionDivider: {
    borderTop: `1px solid ${theme.palette.colors?.fillStroke?.[200] || '#E0E0E0'}`,
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  addButton: {
    marginTop: theme.spacing(2),
  },
}));
